import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { PageNode, PAGE_COLORS, ConnectionSide, PageType, Connection, COMPONENT_LIBRARY } from '@/types/sitemap';
import { Trash2, Sparkles, ChevronDown, ChevronUp, X, GripVertical, Palette, Copy, Pencil, Plus, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Search, MessageCircle } from 'lucide-react';
import { AddPagePopover } from './AddPagePopover';
import { safeLocalStorage } from '@/lib/storage-utils';

interface Props {
  page: PageNode;
  isSelected: boolean;
  isConnecting: boolean;
  isConnectSource: boolean;
  allPages: PageNode[];
  onBatchPositionChange: (updates: { id: string; x: number; y: number }[]) => void;
  onSelect: (id: string, additive?: boolean) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  onRemoveSection: (pageId: string, sectionId: string) => void;
  onDuplicateSection: (pageId: string, sectionId: string) => void;
  onMoveSectionToPage: (fromPageId: string, sectionId: string, toPageId: string, toIndex?: number) => void;
  onReorderSections: (pageId: string, fromIndex: number, toIndex: number) => void;
  onGenerateAllCopy: (pageId: string) => void;
  onStartConnection: (pageId: string, side: ConnectionSide, e: React.MouseEvent) => void;
  onCompleteConnection: (pageId: string, side: ConnectionSide) => void;
  onChangeColor: (pageId: string, color: string) => void;
  onChangeSectionColor: (pageId: string, sectionId: string, color: string) => void;
  onEditSection: (pageId: string, sectionId: string) => void;
  onAddPageRelative: (parentId: string, direction: ConnectionSide, type: PageType, name: string) => void;
  onSwapPagePositions: (pageId1: string, pageId2: string) => void;
  onAddSection: (pageId: string, sectionName: string) => void;
  onEditPage: (pageId: string) => void;
  onCommentSection: (pageId: string, sectionId: string) => void;
  onCommentPage: (pageId: string) => void;
  activeMode: 'sitemap' | 'comment';
  connections: Connection[];
  zoom: number;
  editingSectionId?: string | null;
}

const NODE_WIDTH = 280;
const H_GAP = 60;
const V_GAP_HOME = 100;
const V_GAP_DEFAULT = 40;
const NODE_DEFAULT_HEIGHT = 300;
const SNAP_SIZE = 20;
const snap = (v: number) => Math.round(v / SNAP_SIZE) * SNAP_SIZE;

const STORAGE_PREFIX = 'sitemap-comments-';

/** Read comment count for a thread from localStorage */
function getCommentCount(threadId: string): number {
  try {
    const stored = safeLocalStorage.getItem(STORAGE_PREFIX + threadId);
    if (!stored) return 0;
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

// Get all descendant page IDs connected from a given page
function getDescendants(pageId: string, connections: Connection[]): string[] {
  const descendants: string[] = [];
  const visited = new Set<string>();
  const queue = [pageId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);
    for (const conn of connections) {
      // Follow connections where current is the "from" (parent) side
      if (conn.fromId === current && !visited.has(conn.toId)) {
        descendants.push(conn.toId);
        queue.push(conn.toId);
      }
    }
  }
  return descendants;
}

// Check if a page is a child (appears as toId in any connection)
function isChildPage(pageId: string, connections: Connection[]): boolean {
  return connections.some(c => c.toId === pageId);
}

// Get the sibling in a given direction for a child page
function getSibling(pageId: string, direction: 'up' | 'down' | 'left' | 'right', connections: Connection[]): string | null {
  if (direction === 'up') {
    const parentConn = connections.find(c => c.toId === pageId && c.fromSide === 'bottom');
    return parentConn ? parentConn.fromId : null;
  } else if (direction === 'down') {
    const childConn = connections.find(c => c.fromId === pageId && c.fromSide === 'bottom');
    return childConn ? childConn.toId : null;
  } else if (direction === 'left') {
    const parentConn = connections.find(c => c.toId === pageId && c.fromSide === 'right');
    return parentConn ? parentConn.fromId : null;
  } else {
    const childConn = connections.find(c => c.fromId === pageId && c.fromSide === 'right');
    return childConn ? childConn.toId : null;
  }
}

export function PageNodeCard({
  page,
  isSelected,
  isConnecting,
  isConnectSource,
  allPages,
  onBatchPositionChange,
  onSelect,
  onDelete,
  onDuplicate,
  onRemoveSection,
  onDuplicateSection,
  onMoveSectionToPage,
  onReorderSections,
  onGenerateAllCopy,
  onStartConnection,
  onCompleteConnection,
  onChangeColor,
  onChangeSectionColor,
  onEditSection,
  onAddPageRelative,
  onSwapPagePositions,
  onAddSection,
  onEditPage,
  onCommentSection,
  onCommentPage,
  activeMode,
  connections,
  zoom,
  editingSectionId,
}: Props) {
  const [expanded, setExpanded] = useState(true);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [sectionColorPicker, setSectionColorPicker] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [draggingSectionIndex, setDraggingSectionIndex] = useState<number | null>(null);
  const [addPopover, setAddPopover] = useState<{ direction: ConnectionSide; position: { x: number; y: number } } | null>(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [sectionSearch, setSectionSearch] = useState('');
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const addSectionRef = useRef<HTMLDivElement>(null);
  const sectionSearchInputRef = useRef<HTMLInputElement>(null);
  const nodeRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragStartPos = useRef({ mouseX: 0, mouseY: 0, pageX: 0, pageY: 0 });
  const descendantOffsets = useRef<{ id: string; dx: number; dy: number }[]>([]);

  const isChild = useMemo(() => isChildPage(page.id, connections), [page.id, connections]);

  // Read real comment counts from localStorage and listen for changes
  const refreshCommentCounts = useCallback(() => {
    const counts: Record<string, number> = {};
    // Page-level comments
    counts[`page-${page.id}`] = getCommentCount(`page-${page.id}`);
    // Section-level comments
    for (const section of page.sections) {
      const key = `section-${page.id}-${section.id}`;
      counts[key] = getCommentCount(key);
    }
    setCommentCounts(counts);
  }, [page.id, page.sections]);

  useEffect(() => {
    refreshCommentCounts();
    // Listen for cross-tab storage events
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith(STORAGE_PREFIX)) refreshCommentCounts();
    };
    // Listen for same-tab custom event dispatched by CommentThread
    const handleCustom = () => refreshCommentCounts();
    window.addEventListener('storage', handleStorage);
    window.addEventListener('sitemap-comments-changed', handleCustom);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('sitemap-comments-changed', handleCustom);
    };
  }, [refreshCommentCounts]);

  const filteredSections = useMemo(() => {
    if (!sectionSearch) return COMPONENT_LIBRARY;
    return COMPONENT_LIBRARY.filter(c => c.name.toLowerCase().includes(sectionSearch.toLowerCase()));
  }, [sectionSearch]);

  useEffect(() => {
    if (!showAddSection) return;
    sectionSearchInputRef.current?.focus();
    const handler = (e: MouseEvent) => {
      if (addSectionRef.current && !addSectionRef.current.contains(e.target as Node)) {
        setShowAddSection(false);
        setSectionSearch('');
      }
    };
    setTimeout(() => document.addEventListener('mousedown', handler), 0);
    return () => document.removeEventListener('mousedown', handler);
  }, [showAddSection]);

  const isDraggable = !isChild;

  // Get available swap directions for child pages
  const swapDirections = useMemo(() => {
    if (!isChild) return { up: false, down: false, left: false, right: false };
    return {
      up: getSibling(page.id, 'up', connections) !== null,
      down: getSibling(page.id, 'down', connections) !== null,
      left: getSibling(page.id, 'left', connections) !== null,
      right: getSibling(page.id, 'right', connections) !== null,
    };
  }, [isChild, page.id, connections]);

  const hasAnySwap = swapDirections.up || swapDirections.down || swapDirections.left || swapDirections.right;

  const handleSwap = useCallback((direction: 'up' | 'down' | 'left' | 'right') => {
    const sibling = getSibling(page.id, direction, connections);
    if (sibling) {
      onSwapPagePositions(page.id, sibling);
    }
  }, [page.id, connections, onSwapPagePositions]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button, input, textarea, [data-no-drag], [data-connection-port], [data-edge-add]')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const additive = e.shiftKey;
    onSelect(page.id, additive);

    if (!isDraggable) return;

    // Start drag
    isDragging.current = true;
    dragStartPos.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      pageX: page.x,
      pageY: page.y,
    };

    // Calculate offsets for all descendants
    const descendantIds = getDescendants(page.id, connections);
    descendantOffsets.current = descendantIds.map(id => {
      const p = allPages.find(pg => pg.id === id);
      return p ? { id, dx: p.x - page.x, dy: p.y - page.y } : { id, dx: 0, dy: 0 };
    });

    const handleMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const dx = (ev.clientX - dragStartPos.current.mouseX) / zoom;
      const dy = (ev.clientY - dragStartPos.current.mouseY) / zoom;
      const newX = snap(dragStartPos.current.pageX + dx);
      const newY = snap(dragStartPos.current.pageY + dy);

      // Move this page + all descendants
      const updates = [
        { id: page.id, x: newX, y: newY },
        ...descendantOffsets.current.map(d => ({
          id: d.id,
          x: snap(newX + d.dx),
          y: snap(newY + d.dy),
        })),
      ];
      onBatchPositionChange(updates);
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [page.id, page.x, page.y, onSelect, isDraggable, connections, allPages, zoom, onBatchPositionChange]);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleEdgeAdd = (direction: ConnectionSide, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    const nodeEl = nodeRef.current;
    if (!nodeEl) return;

    let popX = 0, popY = 0;
    if (direction === 'right') { popX = NODE_WIDTH + 16; popY = 0; }
    else if (direction === 'left') { popX = -220; popY = 0; }
    else if (direction === 'bottom') { popX = 0; popY = nodeEl.offsetHeight + 16; }
    else if (direction === 'top') { popX = 0; popY = -260; }

    setAddPopover({ direction, position: { x: popX, y: popY } });
  };

  const handlePopoverSelect = (type: PageType, name: string) => {
    if (addPopover) {
      onAddPageRelative(page.id, addPopover.direction, type, name);
      setAddPopover(null);
    }
  };

  // Reset drag state when sections change (e.g. after cross-page move removes a section)
  useEffect(() => {
    setDraggingSectionIndex(null);
    setDragOverIndex(null);
  }, [page.sections.length]);

  const handleSectionDragStart = (e: React.DragEvent, index: number) => {
    setDraggingSectionIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/section-id', page.sections[index].id);
    e.dataTransfer.setData('application/source-page-id', page.id);
  };

  const handleSectionDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIndex = e.clientY < midY ? index : index + 1;
    if (draggingSectionIndex !== null && dropIndex !== draggingSectionIndex && dropIndex !== draggingSectionIndex + 1) {
      setDragOverIndex(dropIndex);
    } else if (draggingSectionIndex === null) {
      // Cross-page drag
      setDragOverIndex(dropIndex);
    } else {
      setDragOverIndex(null);
    }
  };

  const handleSectionDrop = (e: React.DragEvent) => {
    const sectionId = e.dataTransfer.getData('application/section-id');
    const sourcePageId = e.dataTransfer.getData('application/source-page-id');
    
    if (sourcePageId && sourcePageId !== page.id && sectionId) {
      // Cross-page move — insert at the drop index
      onMoveSectionToPage(sourcePageId, sectionId, page.id, dragOverIndex ?? undefined);
    } else if (draggingSectionIndex !== null && dragOverIndex !== null) {
      const toIndex = dragOverIndex > draggingSectionIndex ? dragOverIndex - 1 : dragOverIndex;
      if (toIndex !== draggingSectionIndex) {
        onReorderSections(page.id, draggingSectionIndex, toIndex);
      }
    }
    setDraggingSectionIndex(null);
    setDragOverIndex(null);
  };

  const handleSectionDragEnd = () => {
    setDraggingSectionIndex(null);
    setDragOverIndex(null);
  };

  const connectionPorts: { side: ConnectionSide; style: React.CSSProperties }[] = [
    { side: 'top', style: { top: -5, left: '50%', transform: 'translateX(-50%)' } },
    { side: 'bottom', style: { bottom: -5, left: '50%', transform: 'translateX(-50%)' } },
    { side: 'left', style: { top: '50%', left: -5, transform: 'translateY(-50%)' } },
    { side: 'right', style: { top: '50%', right: -5, transform: 'translateY(-50%)' } },
  ];

  const showPorts = isSelected || (isConnecting && !isConnectSource);
  // Determine which sides are blocked (connected or would overlap another page)
  const blockedSides = useMemo(() => {
    const sides = new Set<ConnectionSide>();
    // Check connections
    for (const conn of connections) {
      if (conn.fromId === page.id) sides.add(conn.fromSide);
      if (conn.toId === page.id) sides.add(conn.toSide);
    }

    // Check spatial overlap for each direction
    const pageEl = document.querySelector(`[data-page-id="${page.id}"]`) as HTMLElement | null;
    const pageH = pageEl ? pageEl.offsetHeight : NODE_DEFAULT_HEIGHT;
    const isHome = page.type === 'home';
    const vGap = isHome ? V_GAP_HOME : V_GAP_DEFAULT;

    const wouldOverlap = (targetX: number, targetY: number) => {
      return allPages.some(p => {
        if (p.id === page.id) return false;
        const otherEl = document.querySelector(`[data-page-id="${p.id}"]`) as HTMLElement | null;
        const otherH = otherEl ? otherEl.offsetHeight : NODE_DEFAULT_HEIGHT;
        // Check AABB overlap with some margin
        return (
          targetX < p.x + NODE_WIDTH + 20 &&
          targetX + NODE_WIDTH + 20 > p.x &&
          targetY < p.y + otherH + 20 &&
          targetY + NODE_DEFAULT_HEIGHT + 20 > p.y
        );
      });
    };

    if (!sides.has('right') && wouldOverlap(page.x + NODE_WIDTH + H_GAP, page.y)) sides.add('right');
    if (!sides.has('left') && wouldOverlap(page.x - NODE_WIDTH - H_GAP, page.y)) sides.add('left');
    if (!sides.has('bottom') && wouldOverlap(page.x, page.y + pageH + vGap)) sides.add('bottom');
    if (!sides.has('top') && wouldOverlap(page.x, page.y - NODE_DEFAULT_HEIGHT - vGap)) sides.add('top');

    return sides;
  }, [connections, page.id, page.x, page.y, page.type, allPages]);

  // Edge add buttons
  const edgeButtons: { side: ConnectionSide; className: string }[] = [
    { side: 'top', className: 'left-1/2 -translate-x-1/2 -top-8' },
    { side: 'bottom', className: 'left-1/2 -translate-x-1/2 -bottom-8' },
    { side: 'left', className: 'top-1/2 -translate-y-1/2 -left-8' },
    { side: 'right', className: 'top-1/2 -translate-y-1/2 -right-8' },
  ];

  return (
    <div
      data-page-id={page.id}
      ref={nodeRef}
      className={`absolute select-none rounded-xl bg-card overflow-visible transition-shadow duration-150 group/card ${
        isSelected ? 'ring-1 ring-foreground' : 'node-shadow hover:node-shadow-hover'
      } ${isConnectSource ? 'ring-1 ring-foreground/50 opacity-70' : ''}`}
      style={{
        left: page.x,
        top: page.y,
        width: NODE_WIDTH,
        minHeight: 80,
        zIndex: isSelected ? 10 : 1,
        cursor: isDraggable ? 'grab' : 'default',
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      {/* Swap d-pad — above the card, left-aligned */}
      {isChild && hasAnySwap && !isConnecting && (
        <div
          data-no-drag
          className={`absolute left-0 z-30 opacity-0 group-hover/card:opacity-100 transition-opacity ${swapDirections.up && !swapDirections.down ? '-top-10' : '-top-14'}`}
          onClick={e => e.stopPropagation()}
        >
          <div className="grid grid-cols-3 grid-rows-3 gap-[2px]" style={{ width: 48, height: 48 }}>
            {/* row 1 */}
            <div />
            {swapDirections.up
              ? <button onClick={(e) => { e.stopPropagation(); handleSwap('up'); }} title="Move up"
                  className="w-full h-full rounded bg-card border border-border text-muted-foreground flex items-center justify-center hover:bg-foreground hover:text-background shadow-sm transition-colors">
                  <ArrowUp size={10} />
                </button>
              : <div />}
            <div />
            {/* row 2 */}
            {swapDirections.left
              ? <button onClick={(e) => { e.stopPropagation(); handleSwap('left'); }} title="Move left"
                  className="w-full h-full rounded bg-card border border-border text-muted-foreground flex items-center justify-center hover:bg-foreground hover:text-background shadow-sm transition-colors">
                  <ArrowLeft size={10} />
                </button>
              : <div />}
            <div className="rounded bg-muted/50 border border-border" />
            {swapDirections.right
              ? <button onClick={(e) => { e.stopPropagation(); handleSwap('right'); }} title="Move right"
                  className="w-full h-full rounded bg-card border border-border text-muted-foreground flex items-center justify-center hover:bg-foreground hover:text-background shadow-sm transition-colors">
                  <ArrowRight size={10} />
                </button>
              : <div />}
            {/* row 3 */}
            <div />
            {swapDirections.down
              ? <button onClick={(e) => { e.stopPropagation(); handleSwap('down'); }} title="Move down"
                  className="w-full h-full rounded bg-card border border-border text-muted-foreground flex items-center justify-center hover:bg-foreground hover:text-background shadow-sm transition-colors">
                  <ArrowDown size={10} />
                </button>
              : <div />}
            <div />
          </div>
        </div>
      )}
      {/* Edge add (+) buttons */}
      {!isConnecting && edgeButtons.map(btn => {
        const isBlocked = blockedSides.has(btn.side);
        if (isBlocked) return null;
        return (
          <button
            key={btn.side}
            data-edge-add
            onClick={(e) => handleEdgeAdd(btn.side, e)}
            className={`absolute ${btn.className} z-20 w-6 h-6 rounded-full bg-muted-foreground/20 text-muted-foreground border border-border flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-all duration-200 hover:bg-muted-foreground/40 hover:scale-110`}
          >
            <Plus size={14} />
          </button>
        );
      })}

      {/* Add page popover */}
      {addPopover && (
        <AddPagePopover
          position={addPopover.position}
          onSelect={handlePopoverSelect}
          onClose={() => setAddPopover(null)}
        />
      )}

      {/* 4-sided connection ports */}
      {showPorts && connectionPorts.map(port => (
        <div
          key={port.side}
          data-connection-port
          className={`absolute z-20 w-2.5 h-2.5 rounded-full border cursor-crosshair hover:scale-150 transition-transform ${
            isConnecting && !isConnectSource
              ? 'bg-foreground/70 border-foreground/40 animate-pulse'
              : 'bg-foreground border-foreground/50'
          }`}
          style={port.style}
          onMouseDown={(e) => {
            if (!isConnecting) {
              e.stopPropagation();
              onStartConnection(page.id, port.side, e);
            }
          }}
          onMouseUp={(e) => {
            if (isConnecting && !isConnectSource) {
              e.stopPropagation();
              onCompleteConnection(page.id, port.side);
            }
          }}
        />
      ))}

      <div className="rounded-xl overflow-hidden">
        {/* Header */}
        <div
          className="px-4 py-2.5 flex items-center justify-between"
          style={{ backgroundColor: page.color }}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-white truncate">{page.name}</span>
          </div>
          <div className="flex items-center gap-0.5 shrink-0">
            {activeMode === 'comment' ? (() => {
              const count = commentCounts[`page-${page.id}`] || 0;
              const hasUnread = count > 0;
              return (
                <button
                  onClick={(e) => { e.stopPropagation(); onCommentPage(page.id); }}
                  className={`rounded-full transition-all flex items-center gap-1 px-2 py-0.5 ${
                    hasUnread ? 'bg-foreground text-background' : 'bg-white/20 text-white/80 hover:bg-white/30'
                  }`}
                  title="View comments"
                >
                  <MessageCircle size={14} />
                  {hasUnread && (
                    <span className="text-[11px] font-bold">
                      {count}
                    </span>
                  )}
                </button>
              );
            })() : (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); onEditPage(page.id); }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 transition-colors"
                  title="Edit page settings"
                >
                  <Pencil size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDuplicate(page.id); }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 transition-colors"
                  title="Duplicate page"
                >
                  <Copy size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowColorPicker(!showColorPicker); }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 transition-colors"
                >
                  <Palette size={13} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(page.id); }}
                  className="p-1 rounded hover:bg-white/20 text-white/80 transition-colors"
                >
                  <Trash2 size={13} />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Color picker */}
        {showColorPicker && (
          <div className="px-3 py-2 border-b border-border" data-no-drag onClick={e => e.stopPropagation()}>
            <div className="flex flex-wrap gap-1.5">
              {PAGE_COLORS.map((color, i) => (
                <button
                  key={`${color}-${i}`}
                  onClick={() => { onChangeColor(page.id, color); setShowColorPicker(false); }}
                  className={`w-5 h-5 rounded-full transition-transform hover:scale-125 ${page.color === color ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                  style={{ backgroundColor: color }}
                />
              ))}
              <label className="relative w-5 h-5 rounded-full overflow-hidden cursor-pointer border-2 border-dashed border-muted-foreground/40 hover:border-foreground/60 transition-colors flex items-center justify-center" title="Custom color">
                <span className="text-muted-foreground text-[10px] font-bold leading-none">+</span>
                <input
                  type="color"
                  value={page.color}
                  onChange={(e) => onChangeColor(page.id, e.target.value)}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                />
              </label>
            </div>
          </div>
        )}

        {/* Sections toggle */}
        <div className="px-4 py-2 flex items-center justify-between">
          <button
            onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            {expanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            <span className="font-medium">{page.sections.length} sections</span>
          </button>

          {activeMode === 'comment' ? (
            <button
              onClick={(e) => { e.stopPropagation(); /* TODO: mark as read */ }}
              className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-colors"
              title="Mark all comments as read"
            >
              <MessageCircle size={10} /> Mark as read
            </button>
          ) : (
            <button
              onClick={(e) => { e.stopPropagation(); onGenerateAllCopy(page.id); }}
              className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-colors"
              title="Generate all copy"
            >
              <Sparkles size={10} /> Generate all
            </button>
          )}
        </div>

        {/* Sections list */}
        {expanded && (
          <div className="px-3 pb-3"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleSectionDrop(e)}
          >
            {page.sections.map((section, index) => {
              const isEditing = editingSectionId === section.id;
              return (
              <div key={section.id}>
                <div className={`transition-all duration-150 overflow-hidden ${dragOverIndex === index ? 'h-3' : 'h-0'}`}>
                  <div className="h-0.5 mx-2 mt-1.5 rounded-full bg-primary" />
                </div>
                <div
                  draggable
                  data-no-drag
                  onDragStart={(e) => handleSectionDragStart(e, index)}
                  onDragOver={(e) => handleSectionDragOver(e, index)}
                  onDragEnd={handleSectionDragEnd}
                  className={`rounded-lg px-2 py-1.5 mb-1 group relative flex items-start gap-1.5 transition-all ${
                    isEditing ? 'ring-1 ring-primary bg-primary/10' : 'bg-muted hover:bg-muted/80'
                  } ${draggingSectionIndex === index ? 'opacity-30 scale-[0.97]' : ''}`}
                  style={section.color ? { borderLeft: `3px solid ${section.color}` } : undefined}
                >
                  <div
                    data-drag-handle
                    className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                  >
                    <GripVertical size={12} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground truncate">{section.name}</span>
                      <div className={`flex items-center transition-opacity shrink-0 ${
                        activeMode === 'comment' && (commentCounts[`section-${page.id}-${section.id}`] || 0) > 0
                          ? 'opacity-100'
                          : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        {activeMode === 'comment' ? (() => {
                          const count = commentCounts[`section-${page.id}-${section.id}`] || 0;
                          const hasUnread = count > 0;
                          return (
                            <button
                              onClick={(e) => { e.stopPropagation(); onCommentSection(page.id, section.id); }}
                              className={`rounded-full transition-all flex items-center gap-1 px-1.5 py-0.5 ${
                                hasUnread ? 'bg-foreground text-background' : 'bg-muted-foreground/15 text-muted-foreground hover:bg-muted-foreground/25'
                              }`}
                              title="View comments"
                            >
                              <MessageCircle size={13} />
                              {hasUnread && (
                                <span className="text-[10px] font-bold">
                                  {count}
                                </span>
                              )}
                            </button>
                          );
                        })() : (
                          <>
                            <button
                              onClick={(e) => { e.stopPropagation(); onEditSection(page.id, section.id); }}
                              className="p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground transition-colors"
                              title="Edit copy"
                            >
                              <Pencil size={11} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); onDuplicateSection(page.id, section.id); }}
                              className="p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground transition-colors"
                              title="Duplicate section"
                            >
                              <Copy size={11} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); setSectionColorPicker(sectionColorPicker === section.id ? null : section.id); }}
                              className="p-0.5 rounded hover:bg-muted-foreground/20 text-muted-foreground transition-colors"
                              title="Section color"
                            >
                              <Palette size={11} />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); onRemoveSection(page.id, section.id); }}
                              className="p-0.5 rounded hover:bg-destructive/20 text-destructive transition-colors"
                            >
                              <X size={11} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {section.description && (
                      <p className="text-[10px] text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">
                        {section.description}
                      </p>
                    )}
                    {/* Section color picker */}
                    {sectionColorPicker === section.id && (
                      <div className="mt-1.5 flex flex-wrap gap-1" onClick={e => e.stopPropagation()}>
                        {PAGE_COLORS.slice(0, 10).map((color, i) => (
                          <button
                            key={`${color}-${i}`}
                            onClick={() => { onChangeSectionColor(page.id, section.id, color); setSectionColorPicker(null); }}
                            className={`w-4 h-4 rounded-full transition-transform hover:scale-125 ${section.color === color ? 'ring-2 ring-offset-1 ring-foreground' : ''}`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                        <button
                          onClick={() => { onChangeSectionColor(page.id, section.id, ''); setSectionColorPicker(null); }}
                          className="w-4 h-4 rounded-full border border-dashed border-muted-foreground/40 text-[8px] text-muted-foreground flex items-center justify-center hover:border-foreground/60"
                          title="Remove color"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {index === page.sections.length - 1 && (
                  <div className={`transition-all duration-150 overflow-hidden ${dragOverIndex === page.sections.length ? 'h-3' : 'h-0'}`}>
                    <div className="h-0.5 mx-2 mt-1.5 rounded-full bg-primary" />
                  </div>
                )}
              </div>
              );
            })}

            {/* Add Section button */}
            <div className="relative" ref={addSectionRef}>
              <button
                data-no-drag
                onClick={(e) => { e.stopPropagation(); setShowAddSection(!showAddSection); setSectionSearch(''); }}
                className="w-full mt-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg border border-dashed border-border text-xs text-muted-foreground hover:text-foreground hover:border-foreground/30 hover:bg-muted/50 transition-colors"
              >
                <Plus size={12} />
                Add Section
              </button>

              {showAddSection && (
                <div
                  data-no-drag
                  className="absolute left-0 right-0 top-full mt-1 z-50 bg-popover border border-border rounded-xl node-shadow overflow-hidden"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <div className="p-2 border-b border-border flex items-center gap-2">
                    <Search size={13} className="text-muted-foreground shrink-0" />
                    <input
                      ref={sectionSearchInputRef}
                      value={sectionSearch}
                      onChange={e => setSectionSearch(e.target.value)}
                      placeholder="Search sections..."
                      className="flex-1 text-xs bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
                    />
                    <button onClick={() => { setShowAddSection(false); setSectionSearch(''); }} className="text-muted-foreground hover:text-foreground">
                      <X size={13} />
                    </button>
                  </div>
                  <div className="max-h-48 overflow-y-auto scrollbar-none p-1">
                    {filteredSections.map(c => (
                      <button
                        key={c.name}
                        onClick={() => { onAddSection(page.id, c.name); setShowAddSection(false); setSectionSearch(''); }}
                        className="w-full text-left px-3 py-1.5 text-xs rounded-lg text-foreground hover:bg-muted transition-colors"
                      >
                        {c.name}
                      </button>
                    ))}
                    {filteredSections.length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-3">No sections found</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
